// Query hooks of the access area (API-v3 5): sign-in, sign-up, verification,
// onboarding, team, SSO, account and partners. GET /api/me answers the v3
// Me; the kernel's useMe() reads the same cache entry with the v1 type.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import type { Me, OnboardingStep } from "@convenor/shared";
import type {
  ApiKeyCreated,
  BillingDetails,
  BillingDetailsBody,
  BillingInvoicesResponse,
  BillingOverview,
  BillingStatus,
  StartPayingResult,
  ApiKeyDto,
  AuthResult,
  BulkInviteResult,
  ExpertInvitationInfo,
  GetStarted,
  HouseStyle,
  HouseStyleBody,
  InvitationV3,
  JoinInfo,
  JoinRequest,
  LibraryBatch,
  LibraryCommitResult,
  MeV3,
  Member,
  MemberDetail,
  MyJoinRequest,
  NotificationPrefs,
  Onboarding,
  OrgAiSettings,
  OrgProfile,
  OrgProfileBody,
  PartnerBody,
  PartnerDetail,
  PartnerPersonInvited,
  PartnerRow,
  SignupExpertBody,
  SignupTeamBody,
  SsoConnection,
  SsoConnectionBody,
  SsoSettings,
  TemplateSection,
  TotpEnrolStart,
  OrgTwoStepSettings,
} from "@convenor/shared/v3";
import { api, ApiError } from "../../api/client";
import { qk } from "../../api/keys";
import { refreshWhilePending } from "../mail/MailStatusLine";

export const ak = {
  onboarding: ["access", "onboarding"] as const,
  getStarted: ["access", "get-started"] as const,
  orgAi: ["access", "org-ai"] as const,
  orgTwoStep: ["access", "org-two-step"] as const,
  twoStepMethod: ["access", "two-step-method"] as const,
  billing: ["access", "billing"] as const,
  invoices: ["access", "billing", "invoices"] as const,
  houseStyle: ["access", "house-style"] as const,
  library: ["access", "library"] as const,
  joinRequests: ["access", "join-requests"] as const,
  myJoinRequest: ["access", "my-join-request"] as const,
  sso: ["access", "sso"] as const,
  member: (id: string) => ["access", "member", id] as const,
  join: (token: string) => ["access", "join", token] as const,
  expertInvitation: (code: string) => ["access", "expert-invitation", code] as const,
  partners: (projectId: string) => ["access", "partners", projectId] as const,
  partner: (projectId: string, id: string) => ["access", "partner", projectId, id] as const,
};

// ---------- the signed-in person ----------

/** The v3 Me (same cache entry as the kernel's useMe). A 401 is an answer ("signed out"). */
export function useMeV3({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: qk.me, queryFn: () => api.get<MeV3>("/api/me"), retry: false, staleTime: 60_000, enabled });
}

/**
 * After a setup page is saved: go where the person now belongs (Me.home, read fresh,
 * so a new owner without a project goes back to choosing their first project).
 */
export function useGoHome() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    const me = await qc.fetchQuery({ queryKey: qk.me, queryFn: () => api.get<MeV3>("/api/me"), staleTime: 0 });
    navigate(me.home);
  };
}

/** After a sign-in step: start from a clean cache with the new Me (or none). */
export function useApplyAuth() {
  const qc = useQueryClient();
  return (result: AuthResult) => {
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" && q.queryKey[0] !== "meta" });
    if (result.me) qc.setQueryData(qk.me, result.me);
    else void qc.invalidateQueries({ queryKey: qk.me });
  };
}

/** `silentError`: the page shows every failure itself, so no error toast repeats it (queryClient.ts). */
function useAuthMutation<B>(path: string, { silentError = false }: { silentError?: boolean } = {}) {
  const apply = useApplyAuth();
  return useMutation({ mutationFn: (body: B) => api.post<AuthResult>(path, body), onSuccess: apply, meta: silentError ? { silentError: true } : undefined });
}

export const useLogin = () => useAuthMutation<{ email: string; password: string; remember: boolean; audience: "team" | "expert" }>("/api/auth/login");
export const useSignupTeam = () => useAuthMutation<SignupTeamBody>("/api/auth/signup");
export const useSignupExpert = () => useAuthMutation<SignupExpertBody>("/api/auth/signup/expert");
export const useVerifyEmail = () => useAuthMutation<{ code: string }>("/api/auth/verify-email");
// The page shows a used or expired link as its own banner, and anything else in its error box.
export const useResetPassword = () => useAuthMutation<{ token: string; password: string; signOutOthers: boolean }>("/api/auth/reset-password", { silentError: true });
export const useConsumeLink = () => useAuthMutation<{ token: string }>("/api/auth/magic-link/consume");
export const useTwoStepVerify = () => useAuthMutation<{ code: string; trustDevice: boolean }>("/api/auth/2fa/verify");

export function useMagicLink() {
  return useMutation({ mutationFn: (body: { email: string; audience: "team" | "expert"; remember?: boolean }) => api.post<Record<string, never>>("/api/auth/magic-link", body) });
}
export function useForgotPassword() {
  return useMutation({ mutationFn: (body: { email: string }) => api.post<Record<string, never>>("/api/auth/forgot-password", body) });
}
export function useResendCode() {
  return useMutation({ mutationFn: () => api.post<Record<string, never>>("/api/auth/verify-email/resend") });
}
export function useSsoDiscover() {
  return useMutation({ mutationFn: (body: { email: string }) => api.post<{ redirectUrl: string | null }>("/api/auth/sso/discover", body) });
}
export function useTwoStepStart() {
  // currentCode: a code from the current app (or a backup code) when replacing a confirmed authenticator.
  return useMutation({ mutationFn: (currentCode?: string) => api.post<TotpEnrolStart>("/api/auth/2fa/enrol/start", currentCode ? { currentCode } : {}) });
}
export function useTwoStepConfirm() {
  const apply = useApplyAuth();
  return useMutation({
    mutationFn: (body: { code: string }) => api.post<{ backupCodes: string[]; auth: AuthResult }>("/api/auth/2fa/enrol/confirm", body),
    onSuccess: (r) => apply(r.auth),
  });
}
/** M2: how this person passes two-step sign-in ("app", "email", or not set up), and whether codes by email are allowed. */
export type TwoStepMethod = { method: "app" | "email" | null; email: string; emailCodeAllowed: boolean };
export function useTwoStepMethod(enabled = true) {
  return useQuery({ queryKey: ak.twoStepMethod, queryFn: () => api.get<TwoStepMethod>("/api/auth/2fa/method"), enabled, staleTime: Infinity });
}
/** M2: a new code by email (the second step of a sign-in, or to switch codes by email off). */
export function useSendEmailCode() {
  return useMutation({ mutationFn: () => api.post<{ sentTo: string }>("/api/auth/2fa/email/send", {}), meta: { silentError: true } });
}
/** M2: turning codes by email on: a first code to the person's address, then that code. */
export function useEmailCodeStart() {
  return useMutation({ mutationFn: () => api.post<{ sentTo: string }>("/api/auth/2fa/email/enable/start", {}), meta: { silentError: true } });
}
export function useEmailCodeConfirm() {
  const apply = useApplyAuth();
  return useMutation({
    mutationFn: (body: { code: string }) => api.post<{ backupCodes: string[]; auth: AuthResult }>("/api/auth/2fa/email/enable/confirm", body),
    onSuccess: (r) => apply(r.auth),
  });
}
export function useNewBackupCodes() {
  return useMutation({ mutationFn: (body: { currentCode: string }) => api.post<{ backupCodes: string[] }>("/api/auth/2fa/backup-codes", body) });
}
/** DELETE with a JSON body (the kernel client sends none). */
async function deleteWithBody(path: string, body: unknown): Promise<void> {
  const res = await fetch(path, { method: "DELETE", credentials: "include", headers: { Accept: "application/json", "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (res.ok) return;
  const data = (await res.json().catch(() => null)) as { error?: { code: string; message: string; details?: unknown } } | null;
  throw new ApiError(res.status, (data?.error?.code ?? "INTERNAL") as never, data?.error?.message ?? "Something went wrong. Please try again.", data?.error?.details);
}

export function useRemoveTwoStep() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: { currentCode: string }) => deleteWithBody("/api/auth/2fa", body), onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }) });
}
export function useSignupExpertCv() {
  return useMutation({ mutationFn: (body: { fileId: string }) => api.post<void>("/api/auth/signup/expert/cv", body) });
}

export function useExpertInvitation(code: string) {
  return useQuery({
    queryKey: ak.expertInvitation(code),
    queryFn: () => api.get<ExpertInvitationInfo>(`/api/expert-invitations/${encodeURIComponent(code)}`),
    enabled: /^[A-Za-z0-9-]{4,12}$/.test(code),
    retry: false,
  });
}

export function useMyJoinRequest(enabled = true) {
  return useQuery({ queryKey: ak.myJoinRequest, queryFn: () => api.get<MyJoinRequest>("/api/join-requests/mine"), enabled, retry: false });
}

// ---------- invitations accepted at /join/:token ----------

export function useJoinInfo(token: string) {
  return useQuery({ queryKey: ak.join(token), queryFn: () => api.get<JoinInfo>(`/api/join/${encodeURIComponent(token)}`), retry: false });
}
export function useAcceptJoin(token: string) {
  return useAuthMutation<{ name?: string; password?: string; termsAccepted?: boolean }>(`/api/join/${encodeURIComponent(token)}/accept`);
}

// ---------- account ----------

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; notificationPrefs?: Partial<NotificationPrefs> }) => api.patch<MeV3>("/api/me", body),
    onSuccess: (me) => qc.setQueryData(qk.me, me),
  });
}
export function useChangeEmail() {
  return useMutation({ mutationFn: (body: { email: string; currentPassword?: string }) => api.post<{ email: string }>("/api/me/email", body) });
}
export function useConfirmEmail() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: { code: string }) => api.post<MeV3>("/api/me/email/confirm", body), onSuccess: (me) => qc.setQueryData(qk.me, me) });
}
export function useChangePassword() {
  return useMutation({ mutationFn: (body: { currentPassword: string; newPassword: string; signOutOthers: boolean }) => api.post<void>("/api/me/password", body) });
}

// ---------- organisation and onboarding ----------

export function useOrg(enabled = true) {
  return useQuery({ queryKey: qk.org, queryFn: () => api.get<OrgProfile>("/api/org"), enabled });
}
export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OrgProfileBody) => api.post<{ org: OrgProfile; me: MeV3 }>("/api/orgs", body),
    onSuccess: (r) => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" && q.queryKey[0] !== "meta" });
      qc.setQueryData(qk.me, r.me);
      qc.setQueryData(qk.org, r.org);
    },
  });
}
export function useUpdateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<OrgProfileBody> & { defaultTorBoilerplate?: string }) => api.patch<OrgProfile>("/api/org", body),
    onSuccess: (org) => {
      qc.setQueryData(qk.org, org);
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}
/** AI import settings (docs/v3/AI-IMPORT.md 8). */
export function useOrgAi(enabled = true) {
  return useQuery({ queryKey: ak.orgAi, queryFn: () => api.get<OrgAiSettings>("/api/org/ai"), enabled });
}
export function useUpdateOrgAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { enabled?: boolean; capCents?: number; assistantEnabled?: boolean }) => api.patch<OrgAiSettings>("/api/org/ai", body),
    onSuccess: (settings) => qc.setQueryData(ak.orgAi, settings),
  });
}
/** M2: the owner's two-step setting (an authenticator app required, or codes by email allowed). */
export function useOrgTwoStep(enabled = true) {
  return useQuery({ queryKey: ak.orgTwoStep, queryFn: () => api.get<OrgTwoStepSettings>("/api/org/two-step"), enabled });
}
export function useUpdateOrgTwoStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { requireAuthenticator: boolean }) => api.patch<OrgTwoStepSettings>("/api/org/two-step", body),
    onSuccess: (settings) => {
      qc.setQueryData(ak.orgTwoStep, settings);
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}
/** Paying through Mollie (modules/billing): owners and admins only. */
export function useBilling(enabled = true) {
  return useQuery({ queryKey: ak.billing, queryFn: () => api.get<BillingOverview>("/api/billing"), enabled });
}
/** returnTo: where Mollie sends the payer back (the settings page, or "Add payment details"). */
export function useStartPaying() {
  return useMutation({ mutationFn: (returnTo: "settings" | "welcome") => api.post<StartPayingResult>("/api/billing/start", { returnTo }) });
}
/** Saves the billing details (needed before Mollie; printed on every invoice). */
export function useSaveBillingDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BillingDetailsBody) => api.put<BillingDetails>("/api/billing/details", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ak.billing }),
  });
}
export function useBillingInvoices(enabled = true) {
  return useQuery({ queryKey: ak.invoices, queryFn: () => api.get<BillingInvoicesResponse>("/api/billing/invoices"), enabled });
}
/** Cancel the trial, or undo that (owners; modules/trial): nothing is charged on day 30. */
export function useCancelTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (undo: boolean) => api.post<unknown>(undo ? "/api/trial/undo-cancel" : "/api/trial/cancel", {}),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ak.billing }), qc.invalidateQueries({ queryKey: qk.me })]),
  });
}
/** Asks the server to re-check open payments with Mollie (after the checkout sends the payer back). */
export function useCheckBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ status: BillingStatus }>("/api/billing/check", {}),
    // Confirmed payment details can start the free trial: Me changes too.
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ak.billing }), qc.invalidateQueries({ queryKey: qk.me })]),
  });
}
export function useOnboarding(enabled = true) {
  return useQuery({ queryKey: ak.onboarding, queryFn: () => api.get<Onboarding>("/api/org/onboarding"), enabled });
}
/** The "Get started" checklist; read fresh on every visit, as items tick on other screens. */
export function useGetStarted(enabled = true) {
  return useQuery({ queryKey: ak.getStarted, queryFn: () => api.get<GetStarted>("/api/org/get-started"), enabled, staleTime: 0 });
}
export function useHideGetStarted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<GetStarted>("/api/org/get-started/hide"),
    onSuccess: (g) => qc.setQueryData(ak.getStarted, g),
  });
}
export function useSetStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { step: OnboardingStep; state: "done" | "skipped" }) => api.patch<Onboarding>("/api/org/onboarding", body),
    onSuccess: (o) => qc.setQueryData(ak.onboarding, o),
  });
}
export function useFinishOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Onboarding>("/api/org/onboarding/finish"),
    onSuccess: (o) => {
      qc.setQueryData(ak.onboarding, o);
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}
export function useHouseStyle(enabled = true) {
  return useQuery({ queryKey: ak.houseStyle, queryFn: () => api.get<HouseStyle>("/api/org/house-style"), enabled });
}
export function useSaveHouseStyle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: HouseStyleBody) => api.put<HouseStyle>("/api/org/house-style", body),
    onSuccess: (h) => {
      qc.setQueryData(ak.houseStyle, h);
      void qc.invalidateQueries({ queryKey: qk.org });
    },
  });
}
export function useTemplateFromDocx() {
  return useMutation({ mutationFn: (body: { fileId: string }) => api.post<{ sections: TemplateSection[] }>("/api/org/tor-template/from-docx", body) });
}
export function useLibrary(enabled = true) {
  return useQuery({ queryKey: ak.library, queryFn: () => api.get<{ batches: LibraryBatch[] }>("/api/org/library"), enabled });
}
export function useAnalyse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: "contacts" | "cvs"; fileIds: string[] }) => api.post<LibraryBatch>("/api/org/library/analyse", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ak.library }),
  });
}
export function useUseBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; use: boolean }) => api.patch<LibraryBatch>(`/api/org/library/${v.id}`, { use: v.use }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ak.library }),
  });
}
export function useCommitLibrary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<LibraryCommitResult>("/api/org/library/commit"),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ak.library });
      void qc.invalidateQueries({ queryKey: ak.onboarding });
    },
  });
}

// ---------- team ----------

type MembersAnswer = { members: Member[]; canManage: boolean; items: { userId: string; name: string; email: string; role: Member["role"]; joinedAt: string }[] };

/** The team (v3 shape) for the Organisation page. */
export function useTeam() {
  return useQuery({ queryKey: qk.members, queryFn: () => api.get<MembersAnswer>("/api/org/members") });
}

/** v1 shape (userId, name, email, role), kept for other areas (e.g. the contract lead picker). */
export function useMembers() {
  return useQuery({ queryKey: qk.members, queryFn: () => api.get<MembersAnswer>("/api/org/members"), select: (d) => d.items });
}
export function useMember(id: string) {
  return useQuery({ queryKey: ak.member(id), queryFn: () => api.get<MemberDetail>(`/api/org/members/${id}`) });
}
function useInvalidateTeam() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.members });
    void qc.invalidateQueries({ queryKey: qk.invitations });
    void qc.invalidateQueries({ queryKey: ak.joinRequests });
    void qc.invalidateQueries({ queryKey: ["access", "member"] });
  };
}
export function useChangeRole() {
  const done = useInvalidateTeam();
  return useMutation({ mutationFn: (v: { userId: string; role: "admin" | "member" | "viewer" | "expert" }) => api.patch<Member>(`/api/org/members/${v.userId}`, { role: v.role }), onSuccess: done });
}
export function useRemoveMember() {
  const done = useInvalidateTeam();
  return useMutation({ mutationFn: (userId: string) => api.delete(`/api/org/members/${userId}`), onSuccess: done });
}
export function useResetTwoStep() {
  const done = useInvalidateTeam();
  return useMutation({ mutationFn: (userId: string) => api.post<void>(`/api/org/members/${userId}/reset-2fa`), onSuccess: done });
}
export function useInvitations(enabled = true) {
  return useQuery({ queryKey: qk.invitations, queryFn: () => api.get<{ invitations: InvitationV3[] }>("/api/org/invitations"), enabled, select: (d) => d.invitations });
}
export function useInvite() {
  const done = useInvalidateTeam();
  return useMutation({ mutationFn: (body: { email: string; role: "admin" | "member" | "viewer" | "expert"; projectId?: string }) => api.post<InvitationV3>("/api/org/invitations", body), onSuccess: done });
}
export function useBulkInvite() {
  const done = useInvalidateTeam();
  return useMutation({
    mutationFn: (body: { invitations: { email: string; role: "admin" | "member" | "viewer" }[]; allowDomainJoin: boolean }) => api.post<BulkInviteResult>("/api/org/invitations/bulk", body),
    onSuccess: done,
  });
}
export function useResendInvitation() {
  const done = useInvalidateTeam();
  return useMutation({ mutationFn: (id: string) => api.post<InvitationV3>(`/api/org/invitations/${id}/resend`), onSuccess: done });
}
export function useRevokeInvitation() {
  const done = useInvalidateTeam();
  return useMutation({ mutationFn: (id: string) => api.delete(`/api/org/invitations/${id}`), onSuccess: done });
}
export function useJoinRequests(enabled = true) {
  return useQuery({ queryKey: ak.joinRequests, queryFn: () => api.get<{ requests: JoinRequest[] }>("/api/org/join-requests"), enabled, select: (d) => d.requests });
}
export function useDecideJoin() {
  const done = useInvalidateTeam();
  return useMutation({
    mutationFn: (v: { id: string; approve: boolean; role?: "member" | "viewer" }) =>
      v.approve ? api.post<void>(`/api/org/join-requests/${v.id}/approve`, { role: v.role ?? "member" }) : api.post<void>(`/api/org/join-requests/${v.id}/decline`),
    onSuccess: done,
  });
}
export function useSso(enabled = true) {
  return useQuery({ queryKey: ak.sso, queryFn: () => api.get<SsoSettings>("/api/org/sso"), enabled });
}
export function useSaveSso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; body: Partial<SsoConnectionBody> }) => (v.id ? api.patch<SsoConnection>(`/api/org/sso/${v.id}`, v.body) : api.post<SsoConnection>("/api/org/sso", v.body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ak.sso }),
  });
}
/** Looks up the DNS TXT record of each domain of the connection. */
export function useVerifySsoDomains() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.post<SsoConnection>(`/api/org/sso/${id}/verify-domains`, {}), onSuccess: () => qc.invalidateQueries({ queryKey: ak.sso }) });
}
export function useDeleteSso() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.delete(`/api/org/sso/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ak.sso }) });
}

// ---------- partners ----------

export function usePartners(projectId: string) {
  return useQuery({ queryKey: ak.partners(projectId), queryFn: () => api.get<{ partners: PartnerRow[]; canManage: boolean }>(`/api/p/${projectId}/partners`) });
}
export function usePartner(projectId: string, id: string | null) {
  return useQuery({
    queryKey: ak.partner(projectId, id ?? "new"),
    queryFn: () => api.get<PartnerDetail>(`/api/p/${projectId}/partners/${id}`),
    enabled: !!id,
    // EM3: while an invitation email is still on its way, its status refreshes by itself.
    refetchInterval: (q) => refreshWhilePending((q.state.data?.people ?? []).map((p) => p.mail)),
  });
}
export function useSavePartner(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string | null; body: PartnerBody }) => (v.id ? api.patch<PartnerDetail>(`/api/p/${projectId}/partners/${v.id}`, v.body) : api.post<PartnerDetail>(`/api/p/${projectId}/partners`, v.body)),
    onSuccess: (d) => {
      qc.setQueryData(ak.partner(projectId, d.id), d);
      void qc.invalidateQueries({ queryKey: ak.partners(projectId) });
    },
  });
}
export function useInvitePartnerPerson(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { partnerId: string; email: string; roleLabel: string; confirmTeamMember?: boolean }) =>
      api.post<PartnerPersonInvited>(`/api/p/${projectId}/partners/${v.partnerId}/people`, { email: v.email, roleLabel: v.roleLabel, ...(v.confirmTeamMember ? { confirmTeamMember: true } : {}) }),
    onSuccess: (_p, v) => {
      void qc.invalidateQueries({ queryKey: ak.partner(projectId, v.partnerId) });
      void qc.invalidateQueries({ queryKey: ak.partners(projectId) });
    },
  });
}
export function useRemovePartnerPerson(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { partnerId: string; personId: string }) => api.delete(`/api/p/${projectId}/partners/${v.partnerId}/people/${v.personId}`),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: ak.partner(projectId, v.partnerId) });
      void qc.invalidateQueries({ queryKey: ak.partners(projectId) });
    },
  });
}
export function useDeletePartner(projectId: string) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.delete(`/api/p/${projectId}/partners/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ak.partners(projectId) }) });
}

/** The legacy v1 Me type still used by the kernel (the fields overlap). */
export type LegacyMe = Me;
export const isApiCode = (e: unknown, code: string) => e instanceof ApiError && e.code === code;

// ---------- API keys (docs/v3/BULK-IMPORT.md 3) ----------

const apiKeysKey = ["api-keys"] as const;
export function useApiKeys(enabled: boolean) {
  return useQuery({ queryKey: apiKeysKey, queryFn: () => api.get<{ keys: ApiKeyDto[] }>("/api/account/api-keys"), enabled });
}
export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: { name: string; days: number }) => api.post<ApiKeyCreated>("/api/account/api-keys", body), onSuccess: () => qc.invalidateQueries({ queryKey: apiKeysKey }) });
}
export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.delete<void>(`/api/account/api-keys/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: apiKeysKey }) });
}
